import React from 'react';
import PropTypes from 'prop-types';
import NavButton from './NavButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { Link } from 'react-router-dom';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import SiteAdminIcon from '@material-ui/icons/Mood';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Gravatar from 'react-gravatar';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import RecentActorsOutlinedIcon from '@material-ui/icons/RecentActorsOutlined';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import { AuthAPI } from '../../api';
import { AuthStore } from '../../store';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles(theme => ({
  menu: {
    '& .MuiMenu-paper': {
      border: `1px solid ${theme.palette.common.battleshipGrey}`,
    },
  },
  title: {
    padding: '0.5rem 1rem',
    margin: '0 0 0.5rem 0',
    background: theme.palette.common.white,
    maxWidth: '15rem',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    borderBottom: `1px solid ${theme.palette.common.battleshipGrey}`,
  },
  avatar: {
    objectFit: 'cover',
    width: 40,
    height: 40,
  },
  icon: {
    color: theme.palette.primary.main,
  },
}));

const UserNavMenu = ({isAuthenticated=false, userProfile, providerProfile, dispatch, toggleAuthMenu}) => {
  const classes = useStyles();
  const history = useHistory();

  const [showAdminToggle, setShowAdminToggle] = React.useState(false);
  const [toggleText, setToggleText] = React.useState('');
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);

  const openMenu = (event) => setMenuAnchorEl(event.currentTarget);
  const closeMenu = () => setMenuAnchorEl(null);
  const profileLink = (isAuthenticated && userProfile.is_provider) ? '/providers/profile' : '/profile';
  const adminEnabled = history.location.pathname.includes('/admin');

  const handleShowAdminToggle = () => {
    const locationCondition = history.location.pathname.includes('/provider');
    const providerCondition = providerProfile.uuid !== undefined;
    const profileCondition = userProfile.is_provider;
    if (locationCondition && providerCondition && profileCondition) {
      let allowed_providers = userProfile.roles.map(r => r.provider_uuid);
      if (userProfile.is_site_admin || allowed_providers.includes(providerProfile.uuid)) {
        setShowAdminToggle(true);
      }
    } else {
      setShowAdminToggle(false);
    }
  };

  const toggleAdmin = () => {
    if (adminEnabled) {
      history.push(`/provider/${providerProfile.slug}?tab=1`);
    } else {
      history.push(`/provider/${providerProfile.uuid}/admin/profile`);
    }
  };

  const refreshProfile = async () => {
    try {
      const { payload } = await AuthAPI.refreshProfile();
      dispatch(AuthStore.profile(payload.profile));
    } catch (err) {
      if (err.status === 401) dispatch(AuthStore.logout());
    }
  };

  React.useEffect(() => {
    if (isAuthenticated) {
      handleShowAdminToggle();
      (adminEnabled) ? setToggleText('Exit Admin') : setToggleText('Admin Mode');
    }
  }, [providerProfile.uuid, history.location]); // eslint-disable-line

  React.useEffect(() => {
    if (menuAnchorEl !== null) {
      refreshProfile();
    }
  }, [menuAnchorEl]);  // eslint-disable-line

  if (!isAuthenticated) {
    return (
      <React.Fragment>
        <NavButton
          variant="outlined"
          onClick={()=>toggleAuthMenu('signup')}
        >
          Register
        </NavButton>
        <NavButton
          variant="contained"
          color="primary"
          onClick={()=>toggleAuthMenu('login')}
        >
          Login
        </NavButton>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <NavButton onClick={openMenu}>
        <Avatar>
          <Gravatar email={userProfile.email} className={classes.avatar} size={40} default="mp"/>
        </Avatar>
        {menuAnchorEl ? (<KeyboardArrowUpIcon />) : (<KeyboardArrowDownIcon />)}
      </NavButton>

      <Menu
        anchorEl={menuAnchorEl}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(menuAnchorEl)}
        className={classes.menu}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <div className={classes.title}>
          <Typography variant="h3" color="primary">
            {userProfile.username}
          </Typography>
        </div>
        {showAdminToggle ? (
          <MenuItem onClick={toggleAdmin}>
            <ListItemIcon>
              <SettingsOutlinedIcon className={classes.icon}/>
            </ListItemIcon>
            <Typography variant="subtitle2" color="primary">{toggleText}</Typography>
          </MenuItem>
        ) : null}
        <MenuItem component={Link} to={profileLink}>
          <ListItemIcon>
            <RecentActorsOutlinedIcon className={classes.icon}/>
          </ListItemIcon>
          <Typography variant="subtitle2" color="primary">User Profile</Typography>
        </MenuItem>
        <MenuItem component={Link} to='/logout'>
          <ListItemIcon>
            <ExitToAppIcon className={classes.icon}/>
          </ListItemIcon>
          <Typography variant="subtitle2" color="primary">Logout</Typography>
        </MenuItem>
        {userProfile.is_site_admin &&
          <MenuItem component={Link} to='/siteadmin/user'>
            <ListItemIcon>
              <SiteAdminIcon className={classes.icon}/>
            </ListItemIcon>
            <Typography variant="subtitle2" color="primary">Site Admin</Typography>
          </MenuItem>
        }
      </Menu>

    </React.Fragment>
  );
};

UserNavMenu.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  userProfile: PropTypes.object,
  providerProfile: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  toggleAuthMenu: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  return {
    isAuthenticated: state.auth.isAuthenticated,
    userProfile: state.auth.profile,
    providerProfile: state.provider.profile,
  };
};

export default connect(mapStateToProps)(UserNavMenu);
